import { useFirebase, useFirestore } from "react-redux-firebase";
import { useCallback, useMemo, useState } from "react";
import { TaskEvent, TaskState } from "config/firebaseConfig";
import { Collections } from "api/model/Collections";
import pickBy from "lodash/pickBy";
import { StorageFile } from "api/model/StorageFile";
import { useFormikContext } from "formik";
import md5 from "crypto-js/md5";
import { DateTime } from "luxon";

interface UseUploadFilesOptions {
    formFieldName?: string;
    onError?: (error: Error) => void;
    onComplete?: (uploadState: UploadState, storageFile?: StorageFile) => void;
}

export interface UploadState {
    progressPercentage?: number;
    bytesTransferred?: number;
    totalBytes?: number;
    state?: string;
    downloadURL?: string;
    storageFileId?: string;
    originalFileName?: string;
    metadata?: any;
}

const DEFAULT_STATE: UploadState = {
    storageFileId: "",
    originalFileName: "",
    downloadURL: "",
};

interface UseUploadFilesReturn {
    uploadFile: (folder: string, file: File) => void;
    uploadState: UploadState;
    storageFile?: StorageFile;
}

const createStorageFile = (uploadState: UploadState) => ({
    storageFileId: uploadState.storageFileId || "",
    originalFileName: uploadState.originalFileName || "",
    downloadURL: uploadState.downloadURL || "",
    bucket: uploadState.metadata?.bucket || "",
    name: uploadState.metadata?.name || "",
    contentType: uploadState.metadata?.contentType || "",
    fullPath: uploadState.metadata.fullPath || "",
});

export const useUploadFiles = (
    options?: UseUploadFilesOptions
): UseUploadFilesReturn => {
    const { onError, onComplete, formFieldName } = options || {};
    const [uploadState, setUploadState] = useState<UploadState>(DEFAULT_STATE);
    const firebase = useFirebase();
    const firestore = useFirestore();
    const { setFieldValue } = useFormikContext();

    const storageFile = useMemo(() => {
        if (uploadState.state !== TaskState.SUCCESS) return;
        return createStorageFile(uploadState);
    }, [uploadState]);

    const uploadFile = useCallback(
        (folder: string, file: File) => {
            const fileName = md5(
                `${file.name}${DateTime.local().toMillis()}`
            ).toString();
            const storageFolder = firebase.storage().ref(folder);
            const uploadTask = storageFolder.child(fileName).put(file);

            uploadTask.on(
                TaskEvent.STATE_CHANGED,
                function (snapshot) {
                    const { bytesTransferred, totalBytes, state } = snapshot;
                    setUploadState({
                        ...DEFAULT_STATE,
                        bytesTransferred,
                        totalBytes,
                        state,
                        progressPercentage:
                            (snapshot.bytesTransferred / snapshot.totalBytes) *
                            100,
                    });
                },
                function (error) {
                    console.error("error function", error);
                    if (onError) {
                        onError(error);
                    }
                },
                function () {
                    const cleanMetadata = pickBy(
                        uploadTask.snapshot.metadata,
                        (value) => value !== undefined
                    );
                    firestore
                        .collection(Collections.STORAGE_FILES)
                        .add(cleanMetadata)
                        .then((docRef) => {
                            uploadTask.snapshot.ref
                                .getDownloadURL()
                                .then((downloadURL) => {
                                    setUploadState(
                                        (oldState = DEFAULT_STATE) => {
                                            let newState = {
                                                ...oldState,
                                                state:
                                                    uploadTask.snapshot.state,
                                                downloadURL,
                                                metadata: {
                                                    ...uploadTask.snapshot
                                                        .metadata,
                                                },
                                                storageFileId: docRef.id,
                                                originalFileName: file.name,
                                            };
                                            const storageFile = createStorageFile(
                                                newState
                                            );
                                            if (formFieldName) {
                                                setFieldValue(
                                                    formFieldName,
                                                    storageFile
                                                );
                                            }
                                            if (onComplete) {
                                                onComplete(
                                                    newState,
                                                    storageFile
                                                );
                                            }
                                            return newState;
                                        }
                                    );
                                });
                        });
                }
            );
        },
        [firebase, firestore, formFieldName, onComplete, onError, setFieldValue]
    );

    return { uploadFile, uploadState, storageFile };
};
